/**
 * @file: ActiveEntityContext.tsx
 *
 * App-wide pointer to the task / service-record the user is currently viewing.
 * The Task and ServiceRecord flows register themselves on focus; surfaces that
 * live OUTSIDE those flows (the floating chat panel) read it to decide whether
 * to offer an "open order" link — when the order is already the screen behind
 * the surface, the link is redundant.
 */

import { useCreation } from '@euler/utils/hooks';
import { useFocusEffect } from '@react-navigation/native';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { BehaviorSubject } from 'rxjs';

export type ActiveEntity =
  | { kind: 'task'; taskNo: string }
  | { kind: 'serviceRecord'; serviceRecordId: string };

export function sameActiveEntity(
  a: ActiveEntity | null,
  b: ActiveEntity | null,
): boolean {
  if (a == null || b == null || a.kind !== b.kind) return false;
  return a.kind === 'task'
    ? a.taskNo === (b as { taskNo: string }).taskNo
    : a.serviceRecordId === (b as { serviceRecordId: string }).serviceRecordId;
}

type ActiveEntityContextProps = {
  activeEntity$: BehaviorSubject<ActiveEntity | null>;
};

const ActiveEntityContext = createContext<ActiveEntityContextProps>(
  null as any,
);

export const ActiveEntityProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const activeEntity$ = useCreation(
      () => new BehaviorSubject<ActiveEntity | null>(null),
      [],
    );
    const value = useMemo(() => ({ activeEntity$ }), [activeEntity$]);
    return (
      <ActiveEntityContext.Provider value={value}>
        {children}
      </ActiveEntityContext.Provider>
    );
  },
);

// Fallback so consumers rendered outside the provider (tests/storybook) don't
// crash; it just never updates → no active entity tracked.
const fallbackActiveEntity$ = new BehaviorSubject<ActiveEntity | null>(null);

export const useActiveEntity$ = (): BehaviorSubject<ActiveEntity | null> =>
  useContext(ActiveEntityContext)?.activeEntity$ ?? fallbackActiveEntity$;

/** Register the focused screen as the active task / service record. */
export function useRegisterActiveEntity(entity: ActiveEntity) {
  const activeEntity$ = useActiveEntity$();
  const key = entity.kind === 'task' ? entity.taskNo : entity.serviceRecordId;
  useFocusEffect(
    useCallback(() => {
      activeEntity$.next(entity);
      return () => {
        // Clear only if still us — a sibling screen focusing first may have
        // already overwritten the pointer (push A → push B).
        if (sameActiveEntity(activeEntity$.getValue(), entity)) {
          activeEntity$.next(null);
        }
      };
      // entity is reconstructed from (kind, key); depend on the primitives.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEntity$, entity.kind, key]),
  );
}
